import {
  createConditionalNaturalLanguageString,
  formatNumber,
} from "commons/ultilities";
import { flow, types } from "mobx-state-tree";
import { withRootStore } from "mobx-stores/_extension/with-root-store";
import { ReactNode, createElement } from "react";

import { deliveryNoteBusiness } from "api/bussiness/delivery-note/delivery-note-business";
import { GetDeliveryNotes } from "api/models/request/delivery-note/delivery-note";
import { DeliveryNotePagination } from "api/models/response/pagination";
import {
  DeliveryNote,
  IDeliveryNoteProperties,
} from "mobx-stores/_models/delivery-note/delivery-note";
import { DeliveryNoteItem } from "mobx-stores/_models/delivery-note/delivery-note-item";
import { withSetPropAction } from "../_extension/with-set-props";
import { DateRangeFilterStore } from "./common/date-range-filter-store";
import { PaginationStore } from "./common/pagination-store";
export const DeliveryNoteQueryParams = types
  .model({
    poDateRangeFilter: types.optional(DateRangeFilterStore, {}),
    deliveryDateRangeFilter: types.optional(DateRangeFilterStore, {}),
    name: "",
    dealingName: "",
    number: "",
    barcode: "",
    code: "",
    dateRangeFilter: types.optional(DateRangeFilterStore, {}),
    isExactMatch: true,
  })
  .actions(withSetPropAction)
  .views((self) => ({
    get natureLanguageString() {
      const map = new Map<
        string,
        { label: string; value: string | number | null | undefined }
      >([
        ["number", { label: "số phiếu", value: self.number }],
        ["dealingName", { label: "tên giao dịch", value: self.dealingName }],
        ["name", { label: "tên hàng", value: self.name }],
        ["code", { label: "mã hàng", value: self.code }],
        ["barcode", { label: "mã barcode", value: self.barcode }],
      ]);

      return createConditionalNaturalLanguageString(map);
    },
  }));

export const ExcelQueryParams = types
  .model({
    poDateRangeFilter: types.optional(DateRangeFilterStore, {}),
    fromNumber: types.maybeNull(types.number),
    toNumber: types.maybeNull(types.number),
  })
  .actions(withSetPropAction);

export const DeliveryNoteManagement = types
  .model({
    deliveryNotes: types.optional(types.array(DeliveryNote), []),
    pagination: types.optional(PaginationStore, {}),
    deliveryNoteDetail: types.maybeNull(DeliveryNote),
    selectedDeliveryNoteItem: types.maybeNull(DeliveryNoteItem),
    openNew: false,
    openEdit: false,
    openDelete: false,
    openPrintPreview: false,
    openPrint2Preview: false,
    openExportExcel: false,
    queryParams: types.optional(DeliveryNoteQueryParams, {}),
    excelParams: types.optional(ExcelQueryParams, {}),
    searchConditionAsNaturalLanguage: "",
    totalMH: 0,
    totalWeight: 0,
    totalAmount: 0,
  })
  .volatile(() => ({
    searchConditionAsNaturalLanguageNode: null as ReactNode | null,
  }))
  .extend(withRootStore)
  .actions(withSetPropAction)
  .actions((self) => ({
    setSearchConditionAsNaturalLanguageNode(value: ReactNode | null) {
      self.searchConditionAsNaturalLanguageNode = value;
    },
    createSearchConditionAsNaturalLanguageNode(): ReactNode {
      const querySummary = self.queryParams.natureLanguageString;
      const part1 = `Tổng số: ${formatNumber(
        self.pagination.totalItems,
      )} phiếu - Tổng loại: ${formatNumber(self.totalMH)} mặt hàng `;

      const part2 = `Tổng cộng: ${formatNumber(self.totalAmount)} pcs - Trọng lượng: ${formatNumber(self.totalWeight)} kg`;

      const textStyle = {
        color: "blue",
        fontWeight: "bold",
      } as const;

      return createElement(
        "div",
        null,
        querySummary
          ? createElement("div", { style: textStyle }, `${querySummary}`)
          : null,
        createElement("div", { style: textStyle }, part1),
        createElement("div", { style: textStyle }, part2),
      );
    },
    createSearchConditionAsNaturalLanguage() {
      const queryParamsAsNaturalLanguage =
        self.queryParams.natureLanguageString;
      return `${
        queryParamsAsNaturalLanguage ? `${queryParamsAsNaturalLanguage} - ` : ""
      }Tổng cộng: ${formatNumber(self.pagination.totalItems)} phiếu - Tổng loại: ${formatNumber(self.totalMH)} mặt hàng - Tổng cộng: ${formatNumber(self.totalAmount)} pcs - Trọng lượng: ${formatNumber(self.totalWeight)} kg`;
    },
  }))
  .actions((self) => ({
    getDeliveryNotes: flow(function* getDeliveryNotes(
      request?: GetDeliveryNotes,
    ) {
      try {
        self._rootStore.setProp("inProgress", true);
        if (!request) {
          request = {
            filter: self.pagination.filter,
            maxResultCount: self.pagination.itemsPerPage,
            skipCount: self.pagination.skipCount,
            sorting: "",
            fromDate:
              self.queryParams.dateRangeFilter.fromAsDayJs?.isValid() &&
              self.queryParams.dateRangeFilter.from
                ? self.queryParams.dateRangeFilter.from
                : undefined,
            toDate:
              self.queryParams.dateRangeFilter.toAsDayJs?.isValid() &&
              self.queryParams.dateRangeFilter.to
                ? self.queryParams.dateRangeFilter.to
                : undefined,
            dealingName: self.queryParams.dealingName,
            name: self.queryParams.name,
            number: self.queryParams.number,
            code: self.queryParams.code,
            barcode: self.queryParams.barcode,
            isExactMatch: self.queryParams.isExactMatch,
          };
        }
        const res = (yield deliveryNoteBusiness.getLists(
          request,
        )) as DeliveryNotePagination<IDeliveryNoteProperties>;
        self.setProp(
          "deliveryNotes",
          res.items?.map((x, i) => ({
            ...x,
            orderNumber:
              i + 1 + self.pagination.pageIndex * self.pagination.itemsPerPage,
          })),
        );
        self.pagination.setProp("totalItems", res.totalCount);
        self.setProp("totalMH", res.totalMH || 0);
        self.setProp("totalWeight", res.totalWeight || 0);
        self.setProp("totalAmount", res.totalAmount || 0);
        self.setProp(
          "searchConditionAsNaturalLanguage",
          self.createSearchConditionAsNaturalLanguage(),
        );
        self.setSearchConditionAsNaturalLanguageNode(
          self.createSearchConditionAsNaturalLanguageNode(),
        );
      } catch (ex) {
        console.error(ex);
      } finally {
        self._rootStore.setProp("inProgress", false);
      }
    }),
    getDeliveryNoteDetail: flow(function* getDeliveryNoteDetail(id: string) {
      try {
        self._rootStore.setProp("inProgress", true);
        const res = (yield deliveryNoteBusiness.getById(
          id,
        )) as IDeliveryNoteProperties;
        self.setProp("deliveryNoteDetail", res);
      } catch (ex) {
        console.error(ex);
      } finally {
        self._rootStore.setProp("inProgress", false);
      }
    }),
  }));
